package com.imobly.imobly.ui.components.input

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.imobly.imobly.ui.components.messageerror.MessageErrorComp
import com.imobly.imobly.ui.theme.colors.BackGroundColor
import com.imobly.imobly.ui.theme.colors.PrimaryColor
import com.imobly.imobly.ui.theme.colors.ReadOnlyColor
import com.imobly.imobly.ui.theme.fonts.montserratFont
import org.jetbrains.compose.ui.tooling.preview.Preview

private fun formatPhoneInput(input: TextFieldValue): TextFieldValue {
    val digits = input.text.filter { it.isDigit() }.take(11)

    val newText = buildString {
        for (i in digits.indices) {
            if (i == 0) append('(')
            if (i == 2) append(") ")
            if (i == 7) append('-')
            append(digits[i])
        }
    }

    val oldText = input.text
    val oldCursor = input.selection.start.coerceIn(0, oldText.length)

    val newCursor = if (oldCursor >= oldText.length) {
        newText.length
    } else {
        val digitsBeforeCursor = oldText.take(oldCursor).count { it.isDigit() }
        var cursor = 0
        var seenDigits = 0
        for (i in newText.indices) {
            if (newText[i].isDigit()) {
                seenDigits++
            }
            cursor = i + 1
            if (seenDigits >= digitsBeforeCursor) {
                break
            }
        }
        cursor
    }

    val clampedCursor = newCursor.coerceIn(0, newText.length)
    return TextFieldValue(text = newText, selection = TextRange(clampedCursor))
}

@Composable
fun InputPhoneComp(
    label: String,
    placeholder: String = "(00) 00000-0000",
    value: String,
    onValueChange: (String) -> Unit,
    isError: Boolean = false,
    errorMessage: String = "",
    readOnly: Boolean = false,
    modifier: Modifier = Modifier.padding(16.dp).fillMaxWidth()
) {
    var textFieldValue by remember(value) {
        mutableStateOf(TextFieldValue(text = value, selection = TextRange(value.length)))
    }

    if (textFieldValue.text != value) {
        textFieldValue = TextFieldValue(text = value, selection = TextRange(value.length))
    }

    val textColor = if (readOnly) ReadOnlyColor else Color.Black
    val labelColor = if (readOnly) ReadOnlyColor else PrimaryColor
    val indicatorColor = if (readOnly) ReadOnlyColor else PrimaryColor
    val backgroundColor = if (readOnly) BackGroundColor.copy(alpha = 0.5f) else BackGroundColor

    Column(modifier = modifier) {
        OutlinedTextField(
            value = textFieldValue,
            onValueChange = { newValue ->
                val formattedValue = formatPhoneInput(newValue)
                textFieldValue = formattedValue
                onValueChange(formattedValue.text)
            },
            enabled = !readOnly,
            readOnly = readOnly,
            label = {
                Text(
                    text = label,
                    fontFamily = montserratFont(),
                    fontSize = 15.sp,
                    fontWeight = FontWeight.ExtraBold,
                    color = labelColor
                )
            },
            placeholder = {
                Text(
                    text = placeholder,
                    fontFamily = montserratFont(),
                    fontSize = 15.sp,
                    fontWeight = FontWeight.ExtraBold,
                    color = labelColor.copy(alpha = 0.5f)
                )
            },
            shape = RoundedCornerShape(10.dp),
            singleLine = true,
            isError = isError,
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
            textStyle = TextStyle(
                fontFamily = montserratFont(),
                fontSize = 15.sp,
                fontWeight = FontWeight.Bold,
                color = textColor
            ),
            modifier = Modifier.fillMaxWidth(),
            colors = TextFieldDefaults.colors(
                unfocusedTextColor = textColor,
                unfocusedLabelColor = labelColor,
                unfocusedIndicatorColor = indicatorColor,
                unfocusedContainerColor = backgroundColor,
                focusedContainerColor = backgroundColor,
                focusedTextColor = textColor,
                focusedLabelColor = labelColor,
                focusedIndicatorColor = indicatorColor,
                errorContainerColor = backgroundColor,
                errorIndicatorColor = Color.Red,
                errorLabelColor = Color.Red,
                errorTextColor = Color.Red
            )
        )

        if (isError) {
            MessageErrorComp(errorMessage)
        }
    }
}
